import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Video, Users, Calendar, MoreVertical } from 'lucide-react';
import { useWebinarStore } from '@/store/webinar';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function WebinarList() {
  useLanguage();
  const navigate = useNavigate();
  const { webinars, loading, fetchWebinars } = useWebinarStore();

  useEffect(() => {
    fetchWebinars();
  }, [fetchWebinars]);

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">{translateInterfaceText("Webinários")}</h1>
          <p className="text-muted-foreground mt-1">
            {translateInterfaceText("Crie webinários ao vivo, simulados ou just-in-time")}</p>
        </div>
        <Button 
          onClick={() => navigate('/webinarios/criar')}
          className="gap-2"
        >
          <Plus className="h-4 w-4" />
          {translateInterfaceText("Novo Webinário")}</Button>
      </div>

      {/* Empty State */}
      {!loading && webinars.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Video className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-xl font-semibold mb-2">
              {translateInterfaceText("Nenhum webinário criado ainda")}</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              {translateInterfaceText("Crie seu primeiro webinário e comece a converter mais leads com experiências interativas.")}</p>
            <Button 
              onClick={() => navigate('/webinarios/criar')}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              {translateInterfaceText("Criar Primeiro Webinário")}</Button>
          </CardContent>
        </Card>
      )}

      {/* Webinars Grid */}
      {webinars.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {webinars.map((webinar) => (
            <Card 
              key={webinar.id}
              className="group hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate(`/webinarios/${webinar.id}/metricas`)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1 group-hover:text-primary transition-colors">
                      {webinar.title || webinar.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      /{webinar.slug}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={(e) => e.stopPropagation()}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {format(new Date(webinar.created_at), "PPP", { locale: getInterfaceDateLocale() })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>{translateInterfaceText("0 participantes")}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t flex items-center justify-between">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    webinar.status === 'active' ? 'bg-green-100 text-green-800' :
                    webinar.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                    webinar.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {webinar.status === 'active' ? translateInterfaceText("Ativo") :
                     webinar.status === 'draft' ? translateInterfaceText("Rascunho") :
                     webinar.status === 'paused' ? translateInterfaceText("Pausado") : translateInterfaceText("Arquivado")}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/webinarios/editar/${webinar.id}`);
                    }}
                  >
                    {translateInterfaceText("Editar")}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </div>
  );
}
